import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

interface PluginAction {
  label: string;
  pluginName: string;
  pluginInitials: string;
  color: string;
  href: string;
}

const PLUGIN_ACTIONS: PluginAction[] = [
  {
    label: 'Trigger a new audit',
    pluginName: 'Lighthouse',
    pluginInitials: 'LH',
    color: '#3b82f6',
    href: '/lighthouse',
  },
  {
    label: 'Migrate services',
    pluginName: 'Cost Insights',
    pluginInitials: 'CI',
    color: '#8b5cf6',
    href: '/cost-insights',
  },
];

const useStyles = makeStyles(theme => ({
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  createLabel: {
    fontSize: 13,
    fontWeight: 500,
    padding: '6px 12px',
    textTransform: 'none' as const,
    borderRadius: 0,
  },
  chevronBtn: {
    padding: '6px 4px',
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    minWidth: 0,
  },
  chevron: {
    fontSize: 18,
  },
  menuPaper: {
    minWidth: 240,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px 4px',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    opacity: 0.5,
    textTransform: 'uppercase' as const,
  },
  seeAll: {
    fontSize: 11,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  menuItem: {
    padding: '8px 16px',
    fontSize: 13,
  },
  pluginItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 16px',
  },
  pluginAvatar: {
    width: 28,
    height: 28,
    fontSize: 11,
    fontWeight: 600,
  },
  pluginInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  pluginLabel: {
    fontSize: 13,
  },
  pluginSource: {
    fontSize: 10,
    opacity: 0.5,
  },
}));

export function CreateMenu() {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [templates, setTemplates] = useState<
    { name: string; title: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Template' },
        fields: ['metadata.name', 'metadata.title'],
      })
      .then(({ items }) => {
        if (cancelled) return;
        const mapped = items.slice(0, 5).map(e => ({
          name: e.metadata.name,
          title:
            (e.metadata as Record<string, unknown>).title as string ||
            e.metadata.name,
        }));
        setTemplates(mapped);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box className={classes.buttonGroup}>
        <ButtonBase className={classes.createLabel} onClick={handleOpen}>
          Create
        </ButtonBase>
        <ButtonBase className={classes.chevronBtn} onClick={handleOpen}>
          <ExpandMoreIcon className={classes.chevron} />
        </ButtonBase>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper }}
      >
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            Create from template
          </Typography>
          <Typography
            className={classes.seeAll}
            onClick={() => {
              handleClose();
              navigate('/create');
            }}
          >
            See all
          </Typography>
        </Box>
        {templates.map(t => (
          <MenuItem
            key={t.name}
            className={classes.menuItem}
            onClick={() => {
              handleClose();
              navigate(`/create/templates/default/${t.name}`);
            }}
          >
            {t.title}
          </MenuItem>
        ))}
        <Divider />
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            Create from plugins
          </Typography>
        </Box>
        {PLUGIN_ACTIONS.map(action => (
          <MenuItem
            key={action.label}
            className={classes.pluginItem}
            onClick={() => {
              handleClose();
              navigate(action.href);
            }}
          >
            <Avatar
              className={classes.pluginAvatar}
              style={{ backgroundColor: action.color }}
            >
              {action.pluginInitials}
            </Avatar>
            <Box className={classes.pluginInfo}>
              <Typography className={classes.pluginLabel}>
                {action.label}
              </Typography>
              <Typography className={classes.pluginSource}>
                {action.pluginName}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
